import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ListChecks, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Disclaimer } from "@/components/common";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";

function ChecklistCard({ cl, onSave }) {
    const [open, setOpen] = useState(false);
    const [state, setState] = useState(cl.state || {});
    const [progress, setProgress] = useState(cl.progress);

    const recompute = (st) => {
        const done = cl.items.filter((it) => st[it.key]?.done).length;
        setProgress(Math.round((done / cl.items.length) * 100));
    };

    const update = async (key, patch) => {
        const next = { ...state, [key]: { ...(state[key] || {}), ...patch } };
        setState(next); recompute(next);
        try { await api.post("/checklists/progress", { checklist_id: cl.id, item_key: key, ...patch }); }
        catch (e) { toast.error("Erro ao salvar progresso."); }
    };

    return (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm" data-testid={`checklist-${cl.id}`}>
            <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-5 text-left" data-testid={`checklist-toggle-${cl.id}`}>
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <ListChecks className="w-5 h-5 text-petroleum" />
                        <h3 className="font-heading font-bold text-slate-900">{cl.title}</h3>
                    </div>
                    <div className="mt-3 flex items-center gap-3">
                        <Progress value={progress} className="h-2 flex-1" />
                        <span className="text-sm font-bold text-petroleum font-mono w-14 text-right" data-testid={`checklist-progress-${cl.id}`}>{progress}%</span>
                    </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-slate-400 ml-4 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div className="px-5 pb-5 space-y-3 border-t border-slate-100 pt-4">
                    {cl.items.map((it) => {
                        const st = state[it.key] || {};
                        return (
                            <div key={it.key} className="border border-slate-200 rounded-md p-3" data-testid={`checklist-item-${it.key}`}>
                                <label className="flex items-start gap-3 cursor-pointer">
                                    <Checkbox checked={!!st.done} onCheckedChange={(v) => update(it.key, { done: !!v })} data-testid={`check-${it.key}`} className="mt-0.5" />
                                    <span className={`text-sm ${st.done ? "line-through text-slate-400" : "text-slate-700"}`}>{it.label}</span>
                                </label>
                                <div className="mt-3 grid sm:grid-cols-3 gap-2 pl-7">
                                    <Input placeholder="Observação" defaultValue={st.note || ""} onBlur={(e) => e.target.value !== (st.note || "") && update(it.key, { note: e.target.value })} className="text-xs h-8" />
                                    <Input type="date" defaultValue={st.date || ""} onChange={(e) => update(it.key, { date: e.target.value })} className="text-xs h-8" />
                                    <Input placeholder="Responsável" defaultValue={st.responsible || ""} onBlur={(e) => e.target.value !== (st.responsible || "") && update(it.key, { responsible: e.target.value })} className="text-xs h-8" />
                                </div>
                            </div>
                        );
                    })}
                    <p className="text-xs text-slate-400 pt-2">{cl.disclaimer}</p>
                </div>
            )}
        </div>
    );
}

export default function Checklists() {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/checklists").then((r) => setItems(r.data)).catch(() => {}); }, []);

    return (
        <div className="space-y-6" data-testid="checklists-page">
            <PageHeader title="Checklists" subtitle="Organize procedimentos e documentos por tipo de requerimento." />
            <div className="space-y-4">
                {items.map((cl) => <ChecklistCard key={cl.id} cl={cl} />)}
            </div>
            <Disclaimer>Os documentos e procedimentos podem variar conforme regime mineral, substância, situação do processo e regulamentação vigente. Confirme sempre nas fontes oficiais da ANM.</Disclaimer>
        </div>
    );
}
